<script>
  import { _ } from '@sveltia/i18n';
  import { Button } from '@sveltia/ui';
  import { onMount } from 'svelte';

  import {
    exchangeGoogleCredential,
    fetchAuthConfig,
    renderGoogleSignInButton,
  } from '$lib/services/integrations/gitbase/google-auth';

  /**
   * @typedef {Object} Props
   * @property {(token: string) => void | Promise<void>} onSignIn
   */

  /** @type {Props} */
  let { onSignIn } = $props();

  /** @type {HTMLDivElement | undefined} */
  let googleButtonContainer = $state(undefined);
  /** @type {string | undefined} */
  let loadError = $state(undefined);
  /** @type {string | undefined} */
  let signInError = $state(undefined);
  let configLoaded = $state(false);
  let googleEnabled = $state(false);
  /** @type {string | undefined} */
  let googleClientId = $state(undefined);
  let gsiRendered = $state(false);

  const googleSignInLabel = $derived(_('sign_in_with_x', { values: { service: 'Google' } }));

  onMount(async () => {
    const config = await fetchAuthConfig();

    configLoaded = true;
    googleEnabled = !!(config.googleAuthEnabled && config.googleClientId);
    googleClientId = config.googleClientId;
  });

  $effect(() => {
    if (gsiRendered || loadError || !googleEnabled || !googleClientId || !googleButtonContainer) {
      return;
    }

    gsiRendered = true;

    renderGoogleSignInButton(
      googleButtonContainer,
      googleClientId,
      async (credential) => {
        signInError = undefined;

        try {
          const { token } = await exchangeGoogleCredential(credential);
          await onSignIn(token);
        } catch (/** @type {any} */ ex) {
          signInError =
            ex?.status === 403
              ? _('sign_in_error.not_allowed')
              : (ex?.message ?? _('sign_in_error.authentication_aborted'));
        }
      },
    ).catch((/** @type {any} */ ex) => {
      gsiRendered = false;
      loadError = ex?.message ?? 'Could not load Google Sign-In';
    });
  });
</script>

{#if loadError}
  <div role="alert" class="error">{loadError}</div>
{:else if !configLoaded}
  <Button variant="secondary" label={googleSignInLabel} disabled />
{:else if !googleEnabled}
  <Button variant="secondary" label={googleSignInLabel} disabled class="muted" />
{:else}
  <div bind:this={googleButtonContainer} class="google-button"></div>
{/if}
{#if signInError}
  <div role="alert" class="error">{signInError}</div>
{/if}

<style>
  .google-button {
    display: flex;
    justify-content: center;
    min-height: 44px;
    width: 320px;
  }

  .error {
    color: var(--sui-error-foreground-color);
    font-size: var(--sui-font-size-default);
    text-align: center;
  }

  :global(.muted.button) {
    opacity: 0.45;
  }
</style>
